export interface DateSegments {
  year: string;
  month: string;
  day: string;
}

export const EMPTY_DATE_SEGMENTS: DateSegments = {
  year: "",
  month: "",
  day: ""
};

function twoDigits(value: number) {
  return String(value).padStart(2, "0");
}

function normalizeSegment(key: keyof DateSegments, value: string) {
  if (!value) return "";
  if (key === "year") return value;
  return value.padStart(2, "0");
}

/**
 * The segments a field's blur commits, or null when it has nothing to pad. It
 * reads the input's live value: typing a segment's last digit moves focus to
 * the next segment before React commits that digit, so the blur runs with the
 * previous render's segments. Padding the value from them wrote the `0` of a
 * half-typed `03` back as `00` over the month just typed.
 */
export function segmentsOnBlur(
  key: keyof DateSegments,
  liveValue: string,
  segments: DateSegments
): DateSegments | null {
  const normalized = normalizeSegment(key, liveValue);
  return normalized === liveValue ? null : { ...segments, [key]: normalized };
}

export function dateToIso(date: Date) {
  return `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(
    date.getDate()
  )}`;
}

export function segmentsFromIso(value: string): DateSegments {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return { ...EMPTY_DATE_SEGMENTS };
  return {
    year: match[1]!,
    month: match[2]!,
    day: match[3]!
  };
}

function segmentNumber(value: string) {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

export function segmentsToIso(segments: DateSegments): string | null {
  if (!/^\d{4}$/.test(segments.year)) return null;
  const year = segmentNumber(segments.year);
  const month = segmentNumber(segments.month);
  const day = segmentNumber(segments.day);
  if (
    year === null ||
    year < 1000 ||
    year > 9999 ||
    month === null ||
    month < 1 ||
    month > 12 ||
    day === null ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  const candidate = new Date(year, month - 1, day);
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }

  return `${year}-${twoDigits(month)}-${twoDigits(day)}`;
}

export function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function calendarDays(viewMonth: Date) {
  const first = startOfMonth(viewMonth);
  const firstVisible = new Date(
    first.getFullYear(),
    first.getMonth(),
    1 - first.getDay()
  );

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstVisible);
    date.setDate(firstVisible.getDate() + index);
    return date;
  });
}

interface CalendarOpenResolution {
  segments: DateSegments;
  viewMonth: Date;
  selectedIso: string | null;
  shouldCommit: boolean;
}

export function resolveCalendarOpen(
  segments: DateSegments,
  currentView: Date,
  today: Date
): CalendarOpenResolution {
  const empty = !segments.year && !segments.month && !segments.day;
  if (empty) {
    const iso = dateToIso(today);
    return {
      segments: segmentsFromIso(iso),
      viewMonth: startOfMonth(today),
      selectedIso: iso,
      shouldCommit: true
    };
  }

  if (!/^\d{4}$/.test(segments.year)) {
    return {
      segments,
      viewMonth: currentView,
      selectedIso: null,
      shouldCommit: false
    };
  }

  const year = Number(segments.year);
  if (year < 1000 || year > 9999) {
    return {
      segments,
      viewMonth: currentView,
      selectedIso: null,
      shouldCommit: false
    };
  }

  const month = segmentNumber(segments.month);
  const viewMonth =
    month !== null && month >= 1 && month <= 12
      ? new Date(year, month - 1, 1)
      : new Date(year, 0, 1);

  return {
    segments,
    viewMonth,
    selectedIso: segmentsToIso(segments),
    shouldCommit: false
  };
}
